import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  liveCheckAnomalyFailure,
  liveCheckAnomalySuccess,
} from '../../../redux/WeldAnalytics/actions';
import type { NdtIndicationRow, WeldDrillDownRow } from '../components/dashboard/drilldown/drillDownTypes';
import type { OverviewWeldRow } from '../components/WeldOverviewTable';
import type { WeldAnalyticsBundle } from '../types/weld';
import type { DashboardMetrics } from './weldDashboardMetrics';
import { buildDrillDownSessionKey } from './buildDrillDownSessionKey';
import { fetchNdtIndicationLogsApi } from './fetchNdtIndicationLogsApi';
import {
  fetchNdtLogsForPipes,
  indexNdtLogsForRows,
  logsHaveWeldIdentity,
} from './ndtLogIndex';
import type { PassViewRecord } from './fetchWeldViewApis';
import { fetchProjectLimitsApi } from './fetchWeldViewApis';
import type { PipeJobRef } from './jobNumbersFromSetup';
import { prefetchLiveAnomalyScores } from './prefetchLiveAnomalyScores';
import { runWithConcurrency } from './runWithConcurrency';
import {
  clearWeldDetailCacheForSession,
  getCachedWeldDetail,
  setCachedWeldDetail,
  weldDetailCacheKey,
} from './weldDetailCache';
import { buildWeldDrillDownRows, rowKey } from './weldDrillDownModel';
import {
  useWpsViolationsBatchLoader,
  WPS_BATCH_FETCH_CAP,
} from './useWpsViolationsBatch';
import type { LimitsRow, WpsViolationRecord } from './wpsViolationModel';
import { violationsLackLimits } from './wpsViolationModel';

const NDT_BATCH_CONCURRENCY = 8;

export type NdtPrefetchPhase = 'out_of_limit' | 'remaining' | 'idle';

export interface WeldDrillDownPrefetchState {
  sessionKey: string | null;
  allRows: WeldDrillDownRow[];
  ndtCache: Record<string, NdtIndicationRow[]>;
  passesByRowKey: Record<string, PassViewRecord[]>;
  violationsByRowKey: Record<string, WpsViolationRecord[]>;
  welderCache: Record<string, string>;
  limits: LimitsRow[];
  ndtBatchLoading: boolean;
  /** Current NDT wave: out-of-limit first, then remaining welds. */
  ndtPhase: NdtPrefetchPhase;
  /** Target count for the active NDT phase (fail count or remainder count). */
  ndtPhaseTargetCount: number;
  /** Loaded count within the active NDT phase. */
  ndtPhaseLoadedCount: number;
  wpsBatchLoading: boolean;
  ndtBatchError: string | null;
  ndtLoadedCount: number;
  wpsTargetCount: number;
  wpsLoadedCount: number;
  wpsBatchTruncated: boolean;
  liveScoringBatchLoading: boolean;
  liveScoringTargetCount: number;
  liveScoringLoadedCount: number;
  setNdtCache: Dispatch<SetStateAction<Record<string, NdtIndicationRow[]>>>;
  setPassesByRowKey: Dispatch<SetStateAction<Record<string, PassViewRecord[]>>>;
  setViolationsByRowKey: Dispatch<
    SetStateAction<Record<string, WpsViolationRecord[]>>
  >;
  setWelderCache: Dispatch<SetStateAction<Record<string, string>>>;
  setLimits: Dispatch<SetStateAction<LimitsRow[]>>;
  ensureLimitsLoaded: (pid: number) => Promise<LimitsRow[]>;
  cancelPrefetch: () => void;
}

export interface UseWeldDrillDownPrefetchArgs {
  enabled: boolean;
  projectId: number | null;
  /** Fallback pipe id when a row has no production pipe. */
  pipeId: string | null;
  appliedPipeIds: readonly string[];
  appliedJobRefs: readonly PipeJobRef[];
  metrics: DashboardMetrics | null;
  productionWeldData: OverviewWeldRow[];
  bundle: WeldAnalyticsBundle | null;
}

interface LiveScoringEntry {
  anomaly?: Record<string, unknown>;
  loading?: { anomaly?: boolean };
}

export function useWeldDrillDownPrefetch({
  enabled,
  projectId,
  pipeId,
  appliedPipeIds,
  appliedJobRefs,
  metrics,
  productionWeldData,
  bundle,
}: UseWeldDrillDownPrefetchArgs): WeldDrillDownPrefetchState {
  const dispatch = useDispatch();
  const liveByKey = useSelector(
    (state: { weldAnalytics?: { liveScoring?: { byKey?: Record<string, LiveScoringEntry> } } }) =>
      state.weldAnalytics?.liveScoring?.byKey ?? {},
  );
  const liveByKeyRef = useRef(liveByKey);
  liveByKeyRef.current = liveByKey;

  const sessionKey = useMemo(
    () => buildDrillDownSessionKey(projectId, appliedPipeIds, appliedJobRefs),
    [projectId, appliedPipeIds, appliedJobRefs],
  );

  const [ndtCache, setNdtCache] = useState<Record<string, NdtIndicationRow[]>>({});
  const [passesByRowKey, setPassesByRowKey] = useState<Record<string, PassViewRecord[]>>({});
  const [violationsByRowKey, setViolationsByRowKey] = useState<
    Record<string, WpsViolationRecord[]>
  >({});
  const [welderCache, setWelderCache] = useState<Record<string, string>>({});
  const [limits, setLimits] = useState<LimitsRow[]>([]);
  const [ndtBatchLoading, setNdtBatchLoading] = useState(false);
  const [ndtPhase, setNdtPhase] = useState<NdtPrefetchPhase>('idle');
  const [ndtPhaseTargetCount, setNdtPhaseTargetCount] = useState(0);
  const [ndtPhaseLoadedCount, setNdtPhaseLoadedCount] = useState(0);
  const [wpsBatchLoading, setWpsBatchLoading] = useState(false);
  const [ndtBatchError, setNdtBatchError] = useState<string | null>(null);
  const [liveScoringBatchLoading, setLiveScoringBatchLoading] = useState(false);
  const [liveScoringTargetCount, setLiveScoringTargetCount] = useState(0);
  const [liveScoringLoadedCount, setLiveScoringLoadedCount] = useState(0);

  const sessionKeyRef = useRef<string | null>(null);
  const ndtCacheRef = useRef(ndtCache);
  const violationsByRowKeyRef = useRef(violationsByRowKey);
  const ndtInFlightRef = useRef(new Set<string>());
  const ndtRunRef = useRef(0);
  const liveRunRef = useRef(0);
  const limitsLoadedRef = useRef(false);
  const limitsPromiseRef = useRef<Promise<LimitsRow[]> | null>(null);
  const limitsRef = useRef<LimitsRow[]>([]);

  const { loadBatch, cancelBatch } = useWpsViolationsBatchLoader();

  ndtCacheRef.current = ndtCache;
  violationsByRowKeyRef.current = violationsByRowKey;
  limitsRef.current = limits;

  const allRows = useMemo(() => {
    if (!metrics) return [];
    return buildWeldDrillDownRows(metrics, productionWeldData, bundle, projectId);
  }, [metrics, productionWeldData, bundle, projectId]);

  const allRowsRef = useRef(allRows);
  allRowsRef.current = allRows;

  const rowKeysSig = useMemo(() => allRows.map((r) => rowKey(r)).join('\0'), [allRows]);

  const resetCaches = useCallback((prevSession: string | null) => {
    if (prevSession) {
      clearWeldDetailCacheForSession(prevSession);
    }
    setNdtCache({});
    setPassesByRowKey({});
    setViolationsByRowKey({});
    setWelderCache({});
    setLimits([]);
    limitsRef.current = [];
    limitsLoadedRef.current = false;
    limitsPromiseRef.current = null;
    ndtInFlightRef.current.clear();
    setNdtBatchError(null);
    setNdtPhase('idle');
    setNdtPhaseTargetCount(0);
    setNdtPhaseLoadedCount(0);
    setLiveScoringBatchLoading(false);
    setLiveScoringTargetCount(0);
    setLiveScoringLoadedCount(0);
  }, []);

  const cancelPrefetch = useCallback(() => {
    ndtRunRef.current += 1;
    liveRunRef.current += 1;
    cancelBatch();
    setNdtBatchLoading(false);
    setWpsBatchLoading(false);
    setLiveScoringBatchLoading(false);
    setNdtPhase('idle');
  }, [cancelBatch]);

  const ensureLimitsLoaded = useCallback(async (pid: number): Promise<LimitsRow[]> => {
    if (limitsLoadedRef.current) {
      return limitsRef.current;
    }
    if (!limitsPromiseRef.current) {
      const sessionAtFetch = sessionKeyRef.current;
      limitsPromiseRef.current = fetchProjectLimitsApi(pid)
        .then((data) => {
          if (sessionKeyRef.current !== sessionAtFetch) {
            return limitsRef.current;
          }
          limitsRef.current = data.allDataSet;
          setLimits(data.allDataSet);
          limitsLoadedRef.current = true;
          return data.allDataSet;
        })
        .catch((err) => {
          limitsPromiseRef.current = null;
          throw err;
        });
    }
    return limitsPromiseRef.current;
  }, []);

  const resolvePipeIdForRow = useCallback(
    (row: WeldDrillDownRow): number | undefined => {
      const raw =
        row.productionRow?.pipeId ?? row.productionRow?.pipe_details_id ?? pipeId;
      if (raw == null || raw === '') return undefined;
      const n = Number(raw);
      return Number.isFinite(n) ? n : undefined;
    },
    [pipeId],
  );

  // Session change → clear
  useEffect(() => {
    if (sessionKeyRef.current === sessionKey) return;
    const prev = sessionKeyRef.current;
    sessionKeyRef.current = sessionKey;
    cancelPrefetch();
    resetCaches(prev);
  }, [sessionKey, cancelPrefetch, resetCaches]);

  // NDT: Phase A out-of-limit, then Phase B remaining.
  useEffect(() => {
    if (!enabled || !projectId || sessionKey == null || allRows.length === 0) {
      return;
    }

    const rowsMissing = allRowsRef.current.filter(
      (row) => !(rowKey(row) in ndtCacheRef.current),
    );
    if (rowsMissing.length === 0) {
      setNdtBatchLoading(false);
      setNdtPhase('idle');
      return;
    }

    const failRows = rowsMissing.filter((r) => r.outcome === 'fail');
    const restRows = rowsMissing.filter((r) => r.outcome !== 'fail');

    const runId = ndtRunRef.current + 1;
    ndtRunRef.current = runId;
    setNdtBatchLoading(true);
    setNdtBatchError(null);

    void ensureLimitsLoaded(projectId).catch(() => {
      // Limits retry on WPS path.
    });

    let cancelled = false;

    const fetchNdtWave = async (
      waveRows: WeldDrillDownRow[],
      phase: Exclude<NdtPrefetchPhase, 'idle'>,
    ) => {
      if (waveRows.length === 0) return;
      setNdtPhase(phase);
      setNdtPhaseTargetCount(waveRows.length);
      setNdtPhaseLoadedCount(0);

      let ndtCompleted = 0;
      let pendingNdt: Record<string, NdtIndicationRow[]> = {};

      const flushNdtProgress = () => {
        if (Object.keys(pendingNdt).length === 0) return;
        const chunk = pendingNdt;
        pendingNdt = {};
        setNdtCache((prev) => ({ ...prev, ...chunk }));
        if (sessionKey) {
          Object.entries(chunk).forEach(([key, logs]) => {
            const cacheKey = weldDetailCacheKey(sessionKey, key);
            const prev = getCachedWeldDetail(cacheKey);
            setCachedWeldDetail(cacheKey, {
              passes: prev?.passes ?? [],
              violations: prev?.violations ?? [],
              welderIds: prev?.welderIds ?? '—',
              ndtLogs: logs,
              loadedAt: Date.now(),
              wpsError: prev?.wpsError,
            });
          });
        }
      };

      waveRows.forEach((row) => ndtInFlightRef.current.add(rowKey(row)));

      await runWithConcurrency(waveRows, NDT_BATCH_CONCURRENCY, async (row) => {
        const key = rowKey(row);
        if (ndtRunRef.current !== runId || cancelled) return;
        try {
          const rowPipeId = resolvePipeIdForRow(row);
          const logs = await fetchNdtIndicationLogsApi({
            project_id: projectId,
            weld_id: row.weldNumber,
            ...(rowPipeId != null ? { pipe_id: rowPipeId } : {}),
          });
          if (ndtRunRef.current !== runId || cancelled) return;
          pendingNdt[key] = logs;
        } catch {
          if (ndtRunRef.current !== runId || cancelled) return;
          pendingNdt[key] = [];
        } finally {
          ndtInFlightRef.current.delete(key);
        }
        ndtCompleted += 1;
        setNdtPhaseLoadedCount(ndtCompleted);
        if (
          ndtCompleted <= 5 ||
          ndtCompleted % 5 === 0 ||
          ndtCompleted === waveRows.length
        ) {
          flushNdtProgress();
        }
      });

      if (ndtRunRef.current === runId && !cancelled) {
        flushNdtProgress();
      }
      waveRows.forEach((row) => ndtInFlightRef.current.delete(rowKey(row)));
    };

    (async () => {
      try {
        const pipeIds = Array.from(
          new Set(
            rowsMissing
              .map((row) => resolvePipeIdForRow(row))
              .filter((id): id is number => id != null),
          ),
        );
        const logs = await fetchNdtLogsForPipes(projectId, pipeIds);
        if (ndtRunRef.current !== runId || cancelled) return;
        if (logs.length > 0 && !logsHaveWeldIdentity(logs)) {
          await fetchNdtWave(failRows, 'out_of_limit');
          if (ndtRunRef.current !== runId || cancelled) return;
          await fetchNdtWave(restRows, 'remaining');
          return;
        }
        const indexed = indexNdtLogsForRows(
          rowsMissing.map((row) => ({
            key: rowKey(row),
            weldNumber: String(row.weldNumber ?? ''),
            jobNumber: String(row.jobNumber ?? ''),
          })),
          logs,
        );
        setNdtCache((prev) => ({ ...prev, ...indexed }));
        setNdtPhase('remaining');
        setNdtPhaseTargetCount(rowsMissing.length);
        setNdtPhaseLoadedCount(rowsMissing.length);
        if (sessionKey) {
          Object.entries(indexed).forEach(([key, rowLogs]) => {
            const cacheKey = weldDetailCacheKey(sessionKey, key);
            const prev = getCachedWeldDetail(cacheKey);
            setCachedWeldDetail(cacheKey, {
              passes: prev?.passes ?? [],
              violations: prev?.violations ?? [],
              welderIds: prev?.welderIds ?? '—',
              ndtLogs: rowLogs,
              loadedAt: Date.now(),
              wpsError: prev?.wpsError,
            });
          });
        }
      } catch {
        if (ndtRunRef.current === runId && !cancelled) {
          setNdtBatchError('Could not load NDT summaries for all welds.');
        }
      } finally {
        if (ndtRunRef.current === runId && !cancelled) {
          setNdtBatchLoading(false);
          setNdtPhase('idle');
        }
      }
    })();

    return () => {
      cancelled = true;
      ndtRunRef.current += 1;
    };
  }, [
    enabled,
    projectId,
    sessionKey,
    rowKeysSig,
    ensureLimitsLoaded,
    resolvePipeIdForRow,
  ]);

  // WPS pass/zone/tilt prefetch for failed welds (parallel with Phase A NDT)
  useEffect(() => {
    if (!enabled || !projectId || sessionKey == null || allRows.length === 0) {
      return;
    }

    const failedRows = allRowsRef.current.filter((r) => r.outcome === 'fail');
    if (failedRows.length === 0) {
      setWpsBatchLoading(false);
      return;
    }

    const existingKeys = new Set(
      Object.entries(violationsByRowKeyRef.current)
        .filter(([, vs]) => Array.isArray(vs) && !violationsLackLimits(vs))
        .map(([key]) => key),
    );
    const missingRows = failedRows.filter((r) => !existingKeys.has(rowKey(r)));
    if (missingRows.length === 0) {
      setWpsBatchLoading(false);
      return;
    }

    let cancelled = false;
    setWpsBatchLoading(true);

    const mergePartial = (partial: {
      violations: Record<string, WpsViolationRecord[]>;
      welders: Record<string, string>;
      passes: Record<string, PassViewRecord[]>;
    }) => {
      if (cancelled) return;
      setViolationsByRowKey((prev) => ({ ...prev, ...partial.violations }));
      setWelderCache((prev) => ({ ...prev, ...partial.welders }));
      setPassesByRowKey((prev) => ({ ...prev, ...partial.passes }));
      Object.keys(partial.passes).forEach((k) => {
        const cacheKey = weldDetailCacheKey(sessionKey, k);
        const prev = getCachedWeldDetail(cacheKey);
        setCachedWeldDetail(cacheKey, {
          passes: partial.passes[k] ?? [],
          violations: partial.violations[k] ?? [],
          welderIds: partial.welders[k] ?? '—',
          ndtLogs: prev?.ndtLogs,
          loadedAt: Date.now(),
        });
      });
    };

    (async () => {
      let limitsRows: LimitsRow[] | null = null;
      try {
        limitsRows = await ensureLimitsLoaded(projectId);
      } catch {
        limitsRows = null;
      }
      if (cancelled) return;

      const result = await loadBatch(
        projectId,
        missingRows,
        limitsRows,
        existingKeys,
        mergePartial,
      );
      if (cancelled) return;
      setViolationsByRowKey((prev) => ({ ...prev, ...result.violations }));
      setWelderCache((prev) => ({ ...prev, ...result.welders }));
      setPassesByRowKey((prev) => ({ ...prev, ...result.passes }));
      if (result.limits) {
        limitsRef.current = result.limits;
        limitsLoadedRef.current = true;
        setLimits(result.limits);
      }
      setWpsBatchLoading(false);
    })();

    return () => {
      cancelled = true;
      cancelBatch();
    };
  }, [
    enabled,
    projectId,
    sessionKey,
    rowKeysSig,
    loadBatch,
    cancelBatch,
    ensureLimitsLoaded,
  ]);

  // Live anomaly scoring for out-of-limit welds (parallel with Phase A).
  // Skips restart when Redux already has scores for every failed weld (no tab-switch flicker).
  useEffect(() => {
    if (!enabled || !projectId || sessionKey == null || allRows.length === 0) {
      return;
    }

    const failedRows = allRowsRef.current.filter((r) => r.outcome === 'fail');
    if (failedRows.length === 0) {
      setLiveScoringBatchLoading(false);
      setLiveScoringTargetCount(0);
      setLiveScoringLoadedCount(0);
      return;
    }

    const skipKeys = new Set<string>();
    Object.entries(liveByKeyRef.current).forEach(([key, entry]) => {
      if (entry?.anomaly || entry?.loading?.anomaly) {
        skipKeys.add(key);
      }
    });

    const pending = failedRows.filter((r) => !skipKeys.has(rowKey(r)));
    if (pending.length === 0) {
      setLiveScoringBatchLoading(false);
      setLiveScoringTargetCount(failedRows.length);
      setLiveScoringLoadedCount(failedRows.length);
      return;
    }

    const runId = liveRunRef.current + 1;
    liveRunRef.current = runId;
    setLiveScoringBatchLoading(true);
    setLiveScoringLoadedCount(skipKeys.size);
    setLiveScoringTargetCount(failedRows.length);

    let cancelled = false;
    (async () => {
      try {
        const { targetCount } = await prefetchLiveAnomalyScores({
          rows: failedRows,
          projectId,
          skipKeys,
          shouldContinue: () => !cancelled && liveRunRef.current === runId,
          onProgress: (loaded, total) => {
            if (cancelled || liveRunRef.current !== runId) return;
            setLiveScoringTargetCount(total);
            setLiveScoringLoadedCount(loaded);
          },
          onResult: (weldKey, result) => {
            dispatch(
              liveCheckAnomalySuccess({
                weldKey,
                result,
                updatedAt: Date.now(),
              }),
            );
          },
          onError: (weldKey, message) => {
            dispatch(liveCheckAnomalyFailure(message, { weldKey }));
          },
        });
        if (!cancelled && liveRunRef.current === runId) {
          setLiveScoringTargetCount(Math.max(targetCount, failedRows.length));
          setLiveScoringLoadedCount(failedRows.length);
        }
      } finally {
        if (!cancelled && liveRunRef.current === runId) {
          setLiveScoringBatchLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      // Do not bump liveRunRef here when scores already cached — remounts reuse Redux.
      if (pending.length > 0) {
        liveRunRef.current += 1;
      }
    };
  }, [enabled, projectId, sessionKey, rowKeysSig, dispatch]);

  const failedCount = allRows.filter((r) => r.outcome === 'fail').length;
  const wpsTargetCount = Math.min(failedCount, WPS_BATCH_FETCH_CAP);
  const wpsLoadedCount = allRows.filter(
    (r) => r.outcome === 'fail' && rowKey(r) in passesByRowKey,
  ).length;

  return {
    sessionKey,
    allRows,
    ndtCache,
    passesByRowKey,
    violationsByRowKey,
    welderCache,
    limits,
    ndtBatchLoading,
    ndtPhase,
    ndtPhaseTargetCount,
    ndtPhaseLoadedCount,
    wpsBatchLoading,
    ndtBatchError,
    ndtLoadedCount: Object.keys(ndtCache).length,
    wpsTargetCount,
    wpsLoadedCount,
    wpsBatchTruncated: failedCount > WPS_BATCH_FETCH_CAP,
    liveScoringBatchLoading,
    liveScoringTargetCount,
    liveScoringLoadedCount,
    setNdtCache,
    setPassesByRowKey,
    setViolationsByRowKey,
    setWelderCache,
    setLimits,
    ensureLimitsLoaded,
    cancelPrefetch,
  };
}
